import { ColDef, ColGroupDef, GridOptions, IRowNode, ValueGetterParams } from 'ag-grid-community'
import { AuditDetail, CollateralBasketDetail, OrderDetail } from 'api'
import { Locale, Translation } from 'locale'
import {
  AssignableNameCellRenderer,
  axeTradeConditionKeyCreator,
  createAxeTradeConditionRenderer,
  dateFilterParams,
  getCollectionNameText,
  gridOptionsBase,
  groupingRowColDef,
  StandardGridColumnId,
  TypedValueGetterParams,
  withAllContext
} from 'view/components/datagrid'
import {
  axeTradeConditionFilterTextGetter,
  booleanFormatter,
  callableTypeFormatter,
  collectionTypeFormatter,
  dateQuickFilterTextGetter,
  getClearingStatus,
  getOwnDirection,
  getSettlementVenueName,
  GridAxeTradeConditionButtons,
  haircutTypeFormatter,
  makeCusipGetter,
  makeDateFormatter,
  makeExecutingTradingEntityNameGetter,
  makeExternalNegotiationIdGetter,
  makeExternalOrderIdGetter,
  makeExternalQuoteIdGetter,
  makeExternalQuoteRespIdGetter,
  makeHaircutFilterGetter,
  makeHaircutFormatter,
  makeHaircutQuickFilterTextGetter,
  makeIsinGetter,
  makeNotionalFilterGetter,
  makeNotionalFormatter,
  makeNumberFilterGetter,
  makeNumberFormatter,
  makeNumberQuickFilterTextGetter,
  makeOrderIdGetter,
  makeOrganisationFieldGetter,
  makeRateFilterGetter,
  makeRateFormatter,
  makeRateQuickFilterTextGetter,
  makeSponsorNameGetter,
  makeTimeFormatter,
  makeTradingEntityFieldGetter,
  makeTradingEntityLeiGetter,
  makeTradingEntityNameGetter,
  NOTE_INDEXES,
  notionalHeaderValueGetter,
  orderTermFormatter,
  orderTermQuickFilterTextGetter,
  parseCollectionKey,
  repoClassificationFormatter,
  rfqTypeFormatter,
  timeQuickFilterTextGetter
} from 'view/shared/datagrid'
import { AuditGridColumnId } from './AuditGridColumnNames'
import { AuditActionsButton } from './AuditActionsButton'
import { AuditStatusRenderer } from './AuditStatusRenderer'
import {
  ClearingGridColumnId,
  CollateralBasketColumnId,
  CollectionColumnId,
  TREE_DATA_GROUP_BY_COLLECTION_KEY
} from 'view/routes/ColumnNames'
import { createAuditActionsRenderer } from './AuditActionsRenderer'
import { DecisionMakerHelper, MarketHelper } from 'model'
import { CompletedGridColumnId } from 'view/routes/requests/completed/CompletedNegotiationsGrid/CompletedGridColumnNames'
import {
  BASE_ASSET_TYPE,
  BASE_DIRECTION,
  BASE_ISSUE,
  BASE_MARKET_TYPE,
  BASE_REPO_TYPE,
  BASE_ROE,
  BASE_ROS
} from 'view/shared/datagrid/baseColumns'

const makeNoteColumn = (index: string) => ({
  colId: `${AuditGridColumnId.NOTES}_${index}`,
  valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.notes?.values[index],
  headerName: Locale.translate(Translation.TC_NOTE, { noteIndex: parseInt(index, 10) + 1 }),
  width: 180,
  filter: 'agSetColumnFilter',
  hide: true
})

const commonAuditGridColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    ...groupingRowColDef,
    headerName: Locale.translate(Translation.TC_RFQ_ID),
    width: 100,
    minWidth: 100,
    headerComponent: undefined,
    headerComponentParams: {
      alwaysVisible: true
    },
    cellRendererParams: {
      ...groupingRowColDef.cellRendererParams,
      columnKeyFormatters: { [TREE_DATA_GROUP_BY_COLLECTION_KEY]: (key: string) => parseCollectionKey(key) },
      isGroupByLevel: true
    }
  },
  {
    colId: StandardGridColumnId.ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 170,
    minWidth: 170,
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createAuditActionsRenderer(AuditActionsButton)),
    pinned: 'left',
    lockPinned: true,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_STATUS,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.status,
    headerName: Locale.translate(Translation.TC_STATUS),
    width: 140,
    filter: 'agSetColumnFilter',
    cellRenderer: AuditStatusRenderer,
    cellRendererParams: {
      isGroupByLevel: true
    },
    equals: () => false,
    lockVisible: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_ORDER_ID,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.parentOrderId,
    headerName: Locale.translate(Translation.TC_RFQ_ID),
    width: 100,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: CompletedGridColumnId.NEGOTIATION_ID,
    valueGetter: (params: ValueGetterParams<AuditDetail>) => params.data?.negotiation.negotiationId,
    headerName: Locale.translate(Translation.TC_NEGOTIATION_ID),
    width: 100,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_TRADE_DATE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.tradedAt,
    headerName: Locale.translate(Translation.TC_DEAL_DATE),
    width: 130,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter,
    enableRowGroup: false
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_TRADE_TIME,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.tradedAt,
    headerName: Locale.translate(Translation.TC_DEAL_TIME),
    width: 130,
    filter: false,
    valueFormatter: makeTimeFormatter(),
    getQuickFilterText: timeQuickFilterTextGetter,
    enableRowGroup: false
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_UPDATED_AT_DATE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.lastUpdatedAt,
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_DATE),
    width: 130,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_UPDATED_AT_TIME,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.lastUpdatedAt,
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT_TIME),
    width: 130,
    filter: false,
    valueFormatter: makeTimeFormatter(),
    getQuickFilterText: timeQuickFilterTextGetter
  },
  {
    colId: ClearingGridColumnId.COUNTERPARTY_CLEARING,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data && getClearingStatus(params.data),
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_CLEARED),
    width: 70,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.COUNTERPARTY_NAME,
    valueGetter: makeTradingEntityNameGetter(false),
    headerName: Locale.translate(Translation.TC_COUNTERPARTY),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: ClearingGridColumnId.COUNTERPARTY_SPONSOR,
    valueGetter: makeSponsorNameGetter(true),
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_SPONSOR),
    width: 170,
    filter: 'agSetColumnFilter'
  },
  {
    colId: ClearingGridColumnId.CLEARING_VENUE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.clearingHouse?.publishedName,
    headerName: Locale.translate(Translation.TC_CLEARING_VENUE),
    width: 150,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: ClearingGridColumnId.EXECUTING_COUNTERPARTY_NAME,
    valueGetter: makeExecutingTradingEntityNameGetter(),
    headerName: Locale.translate(Translation.TC_EXECUTING_COUNTERPARTY),
    width: 170,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.COLLATERAL_NAME,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.collateral?.name ?? params.data?.collateralBasket?.name,
    headerName: Locale.translate(Translation.TC_COLLATERAL),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollateralBasketColumnId.COLLATERAL_BASKET_DESCRIPTION,
    headerName: Locale.translate(Translation.TC_BASKET_DESCRIPTION),
    valueGetter: (params: TypedValueGetterParams<OrderDetail>) => params.data?.collateralBasket?.description,
    width: 220,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: CollateralBasketColumnId.COLLATERAL_BASKET_DBV_CLASS,
    headerName: Locale.translate(Translation.TC_DBV_CLASS),
    valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket?.dbvClass,
    width: 120,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.COLLATERAL_CURRENCY,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.currency,
    headerName: Locale.translate(Translation.TC_CURRENCY),
    width: 90,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_RFQ_TYPE,
    headerName: Locale.translate(Translation.TC_RFQ_TYPE),
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.rfqType,
    valueFormatter: rfqTypeFormatter,
    width: 100,
    filter: 'agSetColumnFilter'
  },
  {
    ...BASE_DIRECTION,
    colId: AuditGridColumnId.NEGOTIATION_DIRECTION,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) =>
      params.data && getOwnDirection(params.data.negotiation.direction, params.data.ownOrder),
    headerName: Locale.translate(Translation.TC_ABBREVIATED_DIRECTION),
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_NOTIONAL,
    headerValueGetter: notionalHeaderValueGetter,
    headerName: Locale.translate(Translation.TC_NOTIONAL_SHORT),
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.notional,
    width: 110,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNotionalFormatter(),
    filterValueGetter: makeNotionalFilterGetter()
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_RATE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.rate,
    headerName: Locale.translate(Translation.TC_RATE),
    width: 80,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    cellClass: 'font-weight-bold ag-numeric-cell',
    valueFormatter: makeRateFormatter(),
    filterValueGetter: makeRateFilterGetter(),
    getQuickFilterText: makeRateQuickFilterTextGetter()
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_TERM,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.term,
    headerName: Locale.translate(Translation.TC_TERM),
    width: 120,
    filter: 'agSetColumnFilter',
    valueFormatter: orderTermFormatter,
    getQuickFilterText: orderTermQuickFilterTextGetter
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_START_DATE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.startDate,
    headerName: Locale.translate(Translation.TC_START_DATE),
    width: 120,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_END_DATE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.endDate,
    headerName: Locale.translate(Translation.TC_END_DATE),
    width: 120,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter
  },
  {
    colId: CompletedGridColumnId.MARKET_TRADE_DATE,
    headerName: Locale.translate(Translation.TC_MARKET_TRADE_DATE),
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params?.data?.negotiation?.marketTradeDate,
    width: 150,
    filter: 'agSetColumnFilter',
    valueFormatter: makeDateFormatter()
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_HAIRCUT_TYPE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.haircutType,
    headerName: Locale.translate(Translation.TC_COLLATERAL_HAIRCUT),
    valueFormatter: haircutTypeFormatter,
    width: 100,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_HAIRCUT,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.haircut,
    headerName: Locale.translate(Translation.TC_HAIRCUT),
    equals: () => false,
    width: 80,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeHaircutFormatter(),
    filterValueGetter: makeHaircutFilterGetter(),
    getQuickFilterText: makeHaircutQuickFilterTextGetter()
  },
  {
    colId: AuditGridColumnId.REFERENCE_RATE_NAME,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.referenceRate?.name,
    headerName: Locale.translate(Translation.TC_REFERENCE_RATE),
    width: 120,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_SETTLEMENT_VENUE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) =>
      params.data && getSettlementVenueName(params.data?.negotiation.settlementVenue),
    headerName: Locale.translate(Translation.TC_SETTLEMENT),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_CSD,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.csd,
    headerName: Locale.translate(Translation.TC_CSD),
    width: 100,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_AXE_ROLL_UNWIND,
    headerName: Locale.translate(Translation.TC_AXE_ROLL_UNWIND),
    valueGetter: ({ data }: TypedValueGetterParams<AuditDetail>) => data?.axeTradeCondition,
    equals: () => false,
    cellRenderer: withAllContext(createAxeTradeConditionRenderer(GridAxeTradeConditionButtons)),
    keyCreator: axeTradeConditionKeyCreator,
    getQuickFilterText: axeTradeConditionFilterTextGetter,
    valueFormatter: axeTradeConditionFilterTextGetter,
    width: 130,
    filter: false,
    sortable: false
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_CALLABLE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.callableType,
    headerName: Locale.translate(Translation.TC_CALLABLE),
    width: 100,
    filter: 'agSetColumnFilter',
    valueFormatter: callableTypeFormatter
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_CALL_DAYS,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.callablePeriodDays,
    headerName: Locale.translate(Translation.TC_CALL_DAYS),
    width: 100,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter()
  },
  {
    colId: AuditGridColumnId.COLLATERAL_COUPON,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.collateral?.coupon,
    headerName: Locale.translate(Translation.TC_COUPON),
    width: 80,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ percent: true, minDp: 1, decimalShift: 2 }),
    filterValueGetter: makeNumberFilterGetter({ percent: true, minDp: 1, decimalShift: 2 }),
    getQuickFilterText: makeNumberQuickFilterTextGetter({ percent: true }),
    hide: true
  },
  {
    colId: AuditGridColumnId.BOOK_NAME,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) =>
      params.data?.ownOrder ? params.data?.negotiation.initiatorBook : params.data?.negotiation.responderBook,
    headerName: Locale.translate(Translation.TC_BOOK),
    width: 180,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.STRATEGY_NAME,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) =>
      params.data?.ownOrder ? params.data?.negotiation.initiatorStrategy : params.data?.negotiation.responderStrategy,
    headerName: Locale.translate(Translation.TC_STRATEGY),
    width: 180,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.BUNDLE_NAME,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => (params.data?.ownOrder ? params.data?.negotiation.bundle : null),
    headerName: Locale.translate(Translation.TC_BUNDLE),
    width: 180,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.COLLATERAL_ISSUER,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.collateral?.issuer,
    headerName: Locale.translate(Translation.TC_ISSUER),
    width: 80,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.COUNTERPARTY_LEI,
    valueGetter: makeTradingEntityLeiGetter(false),
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_LEI),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.COUNTERPARTY_BIC,
    valueGetter: makeTradingEntityFieldGetter('bic', false),
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_BIC),
    width: 170,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.COUNTERPARTY_FIRM,
    valueGetter: makeOrganisationFieldGetter('legalName', false),
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_FIRM),
    width: 150,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_TRADE_TYPE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.repoClassification,
    headerName: Locale.translate(Translation.TC_TRADE_TYPE),
    width: 100,
    filter: 'agSetColumnFilter',
    valueFormatter: repoClassificationFormatter,
    hide: true
  },
  {
    colId: AuditGridColumnId.COUNTERPARTY_FIRM_LEI,
    valueGetter: makeOrganisationFieldGetter('lei', false),
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_FIRM_LEI),
    width: 150,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.FUND_NAME,
    valueGetter: makeTradingEntityNameGetter(true),
    headerName: Locale.translate(Translation.TC_MY_ENTITY),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.BLOCK_TRADE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.blockTrade,
    headerName: Locale.translate(Translation.TC_BLOCK),
    width: 80,
    filter: 'agSetColumnFilter',
    valueFormatter: booleanFormatter,
    hide: true
  },
  {
    colId: AuditGridColumnId.FUND_LEI,
    valueGetter: makeTradingEntityLeiGetter(true),
    headerName: Locale.translate(Translation.TC_MY_ENTITY_LEI),
    width: 170,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.FUND_BIC,
    valueGetter: makeTradingEntityFieldGetter('bic', true),
    headerName: Locale.translate(Translation.TC_MY_ENTITY_BIC),
    width: 170,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.FUND_FIRM,
    valueGetter: makeOrganisationFieldGetter('legalName', true),
    headerName: Locale.translate(Translation.TC_MY_FIRM),
    width: 150,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.FUND_FIRM_LEI,
    valueGetter: makeOrganisationFieldGetter('lei', true),
    headerName: Locale.translate(Translation.TC_MY_FIRM_LEI),
    width: 170,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    ...BASE_REPO_TYPE,
    colId: AuditGridColumnId.NEGOTIATION_REPO_TYPE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.repoType,
    hide: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_BOND_PRICE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.bondPrice,
    headerName: Locale.translate(Translation.TC_COLLATERAL_PRICE),
    width: 120,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    filterValueGetter: makeNumberFilterGetter()
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_ACCRUED_INTEREST,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.accruedInterest,
    headerName: Locale.translate(Translation.TC_COLLATERAL_ACCRUED_INTEREST),
    width: 180,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ minDp: 2 })
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_START_CONSIDERATION,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.startConsideration,
    headerName: Locale.translate(Translation.TC_START_CONSIDERATION),
    width: 150,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ minDp: 2 })
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_REPO_INTEREST,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.repoInterest,
    headerName: Locale.translate(Translation.TC_REPO_INTEREST),
    width: 100,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ minDp: 2 }),
    hide: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_END_CONSIDERATION,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.endConsideration,
    headerName: Locale.translate(Translation.TC_END_CONSIDERATION),
    width: 150,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter({ minDp: 2 })
  },
  {
    colId: AuditGridColumnId.COLLATERAL_ISIN_OR_BUI,
    valueGetter: makeIsinGetter(),
    headerName: Locale.translate(Translation.TC_ISIN_BUI),
    width: 140,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.COLLATERAL_CUSIP,
    valueGetter: makeCusipGetter(),
    headerName: Locale.translate(Translation.TC_CUSIP),
    width: 120,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.PRODUCT,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.product?.name,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    width: 80,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.PRODUCT_SECTOR,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.productSector?.name,
    headerName: Locale.translate(Translation.TC_SECTOR),
    width: 100,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_MARKET,
    headerName: Locale.translate(Translation.TC_MARKET),
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => {
      const market = params.data?.negotiation.market
      return market && MarketHelper.getName(market)
    },
    width: 80,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: CollectionColumnId.COLLECTION_NAME,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => getCollectionNameText(params.data?.collection),
    headerName: Locale.translate(Translation.TC_COLLECTION_NAME),
    cellRenderer: AssignableNameCellRenderer,
    cellRendererParams: {
      unassignedLabel: Locale.translate(Translation.TC_COLLECTION_UNASSIGNED),
      hideUnassignedLabel: true
    },
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollectionColumnId.COLLECTION_TYPE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.collection?.type,
    headerName: Locale.translate(Translation.TC_COLLECTION_TYPE),
    valueFormatter: collectionTypeFormatter,
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: CollectionColumnId.COLLECTION_ID,
    headerName: Locale.translate(Translation.TC_COLLECTION_ID),
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.collection?.id,
    filter: 'agSetColumnFilter',
    width: 150,
    hide: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_LAST_INITIATOR,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.lastInitiatorId,
    headerName: Locale.translate(Translation.TC_LAST_INITIATOR),
    width: 150,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_LAST_RESPONDER,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.lastResponderId,
    headerName: Locale.translate(Translation.TC_LAST_RESPONDER),
    width: 150,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_UPDATED_BY,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.lastUpdatedBy,
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_BY),
    width: 130,
    filter: 'agSetColumnFilter'
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_CREATED_AT_DATE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.createdAt,
    headerName: Locale.translate(Translation.TC_CREATION_DATE),
    width: 110,
    filter: 'agDateColumnFilter',
    ...dateFilterParams,
    valueFormatter: makeDateFormatter(),
    getQuickFilterText: dateQuickFilterTextGetter,
    hide: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_CREATED_AT_TIME,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.createdAt,
    headerName: Locale.translate(Translation.TC_CREATION_TIME),
    width: 110,
    filter: false,
    valueFormatter: makeTimeFormatter(),
    getQuickFilterText: timeQuickFilterTextGetter,
    hide: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_NOTICE_PERIOD_DAYS,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.noticePeriodDays,
    headerName: Locale.translate(Translation.TC_NOTICE_PERIOD_DAYS),
    width: 80,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    valueFormatter: makeNumberFormatter(),
    hide: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_PRE_AGREED,
    headerName: Locale.translate(Translation.TC_PRE_AGREED),
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.preAgreed,
    valueFormatter: booleanFormatter,
    width: 120,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.NEGOTIATION_MASTER_LEGAL_AGREEMENT,
    headerName: Locale.translate(Translation.TC_MASTER_LEGAL_AGREEMENT),
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.masterLegalAgreementType,
    width: 180,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.INVESTMENT_DECISION_MAKER,
    headerName: Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER),
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => {
      const idm = params.data?.ownOrder ? params.data.initiatorInvestmentDecisionMaker : params.data?.responderInvestmentDecisionMaker
      return idm && DecisionMakerHelper.getName(idm)
    },
    width: 180,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: AuditGridColumnId.EXECUTION_DECISION_MAKER,
    headerName: Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER),
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => {
      const edm = params.data?.ownOrder ? params.data.initiatorExecutionDecisionMaker : params.data?.responderExecutionDecisionMaker
      return edm && DecisionMakerHelper.getName(edm)
    },
    width: 180,
    filter: 'agSetColumnFilter',
    hide: true
  },
  {
    colId: CompletedGridColumnId.EXTERNAL_NEGOTIATION_ID,
    valueGetter: makeExternalNegotiationIdGetter(),
    headerName: Locale.translate(Translation.TC_EXTERNAL_NEGOTIATION_ID),
    width: 180,
    filter: 'agSetColumnFilter',
    enableRowGroup: false,
    hide: true
  },
  {
    colId: CompletedGridColumnId.EXTERNAL_ORDER_ID,
    valueGetter: makeExternalOrderIdGetter(),
    headerName: Locale.translate(Translation.TC_EXTERNAL_ORDER_ID),
    width: 180,
    filter: 'agSetColumnFilter',
    enableRowGroup: false,
    hide: true
  },
  {
    colId: CompletedGridColumnId.ORDER_ID,
    valueGetter: makeOrderIdGetter(),
    headerName: Locale.translate(Translation.TC_ORDER_ID),
    width: 180,
    filter: 'agSetColumnFilter',
    enableRowGroup: false,
    hide: true
  },
  {
    colId: CompletedGridColumnId.NEGOTIATION_AXE_ID,
    valueGetter: (params: ValueGetterParams<AuditDetail>) => params.data?.negotiation.axeId,
    headerName: Locale.translate(Translation.TC_AXE_ID),
    width: 100,
    filter: 'agSetColumnFilter',
    enableRowGroup: false,
    hide: true
  },
  {
    colId: CompletedGridColumnId.EXTERNAL_QUOTE_ID,
    valueGetter: makeExternalQuoteIdGetter(),
    headerName: Locale.translate(Translation.TC_EXTERNAL_QUOTE_ID),
    width: 180,
    filter: 'agSetColumnFilter',
    enableRowGroup: false,
    hide: true
  },
  {
    colId: CompletedGridColumnId.EXTERNAL_QUOTE_RESPONSE_ID,
    valueGetter: makeExternalQuoteRespIdGetter(),
    headerName: Locale.translate(Translation.TC_EXTERNAL_QUOTE_RESPONSE_ID),
    width: 180,
    filter: 'agSetColumnFilter',
    enableRowGroup: false,
    hide: true
  },
  {
    colId: CompletedGridColumnId.MY_ENTITY_SHORT_NAME,
    valueGetter: (params: ValueGetterParams<AuditDetail>) => params.data?.myEntityShortName,
    headerName: Locale.translate(Translation.TC_MY_ENTITY_SHORT_NAME),
    width: 150,
    filter: 'agSetColumnFilter',
    enableRowGroup: false,
    hide: true
  },
  {
    colId: CompletedGridColumnId.MY_FICC_ENTITY_SHORT_NAME,
    headerName: Locale.translate(Translation.TC_MY_FICC_ENTITY_SHORT_NAME),
    valueGetter: (params: ValueGetterParams<AuditDetail>) => params.data?.orderFund?.ficcShortName,
    filter: 'agSetColumnFilter',
    width: 150,
    hide: true
  },
  {
    colId: CompletedGridColumnId.BATCH_ID,
    valueGetter: (params: ValueGetterParams<AuditDetail>) => params.data?.negotiation.batchId,
    headerName: Locale.translate(Translation.TC_BATCH_ID),
    width: 150,
    filter: 'agSetColumnFilter',
    enableRowGroup: false,
    hide: true
  },
  {
    colId: CompletedGridColumnId.FACTOR,
    valueGetter: (params: ValueGetterParams<AuditDetail>) => params.data?.negotiation.factor,
    headerName: Locale.translate(Translation.TC_FACTOR),
    width: 150,
    filter: 'agSetColumnFilter',
    enableRowGroup: false,
    hide: true
  },
  {
    ...BASE_ASSET_TYPE,
    colId: CompletedGridColumnId.ASSET_TYPE,
    valueGetter: (params: ValueGetterParams<AuditDetail>) => params.data?.negotiation.assetType,
    width: 150,
    enableRowGroup: false,
    hide: true
  },
  {
    ...BASE_ISSUE,
    colId: CompletedGridColumnId.ISSUE,
    valueGetter: (params: ValueGetterParams<AuditDetail>) => params.data?.collateral?.issue,
    enableRowGroup: false,
    hide: true
  },
  {
    ...BASE_ROE,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.rightOfExit,
    enableRowGroup: false,
    hide: true
  },
  {
    ...BASE_ROS,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.rightOfSubstitution,
    enableRowGroup: false,
    hide: true
  },
  { ...BASE_MARKET_TYPE, hide: true }
]

const buySideAuditGridColumnDefs = [
  ...NOTE_INDEXES.map(makeNoteColumn),
  {
    colId: AuditGridColumnId.ALLOW_PARTIAL,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation.allowPartial,
    headerName: Locale.translate(Translation.TC_ALLOW_PARTIAL),
    width: 80,
    filter: 'agSetColumnFilter',
    valueFormatter: booleanFormatter,
    hide: true
  },
  {
    colId: CompletedGridColumnId.SUBMITTING_USER,
    valueGetter: (params: TypedValueGetterParams<AuditDetail>) => params.data?.negotiation?.createdBy,
    headerName: Locale.translate(Translation.TC_SUBMITTING_USER),
    width: 250,
    filter: 'agSetColumnFilter',
    hide: true
  }
]

export const auditGridColumnDefs: (isBuySideOrganisation: boolean) => (ColGroupDef | ColDef)[] = isBuySideOrganisation =>
  isBuySideOrganisation ? commonAuditGridColumnDefs.concat(buySideAuditGridColumnDefs) : commonAuditGridColumnDefs

export const auditGridOptions: GridOptions = {
  ...gridOptionsBase,
  defaultColDef: {
    ...gridOptionsBase.defaultColDef,
    enableRowGroup: false,
    sortable: false
  },
  getRowClass: params => {
    const node: IRowNode = params.node
    if (node.level === 0) {
      return 'group-row-level'
    } else {
      return 'nested-row-level'
    }
  },
  treeData: true,
  getDataPath: (data: AuditDetail) => {
    const dataPath = data.auditId.split('/')
    return dataPath.map(path => {
      // Sanitize path name in case has special invalid '/' char
      return path.replace('&#47;', '/')
    })
  },
  groupDefaultExpanded: 0,
  groupSelectsChildren: undefined,
  getRowId: ({ data }) => data.auditId
}
